import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const DEFAULT_API_USAGE = [
  { name: "Text-To-Text", value: 132000, color: "#1C5D42" },
  { name: "Text-To-Speech", value: 105000, color: "#B59928" },
  { name: "Speech-To-Text", value: 68000, color: "#10B981" },
  { name: "Speech Translations", value: 42000, color: "#F97316" },
  { name: "Text-To-Speech Translations", value: 30000, color: "#5A67D8" },
  { name: "Speech-To-Text Translation", value: 25000, color: "#FF5A5F" },
];

const UsageByApiTypeChart = ({ data }) => {
  // Use provided data if available and non-empty; fallback to DEFAULT_API_USAGE
  const hasData = Array.isArray(data) && data.length > 0;
  const rawChartData = hasData ? data : DEFAULT_API_USAGE;

  // Check if all values are 0
  const allZero = rawChartData.every((item) => !item.value || Number(item.value) === 0);
  const chartData = allZero ? DEFAULT_API_USAGE : rawChartData;

  // Find dynamic maximum value
  const maxVal = Math.max(...chartData.map((d) => Number(d.value) || 0), 10);

  // Compute nice dynamic domain & ticks for X-Axis
  const getNiceMaxAndTicks = (max) => {
    if (max <= 10) return { maxDomain: 10, ticks: [0, 2.5, 5, 7.5, 10] };
    if (max <= 50) return { maxDomain: 50, ticks: [0, 12.5, 25, 37.5, 50] };
    if (max <= 100) return { maxDomain: 100, ticks: [0, 25, 50, 75, 100] };
    if (max <= 500) return { maxDomain: 500, ticks: [0, 125, 250, 375, 500] };
    if (max <= 1000) return { maxDomain: 1000, ticks: [0, 250, 500, 750, 1000] };
    
    // For larger numbers (e.g., 140,000)
    if (max <= 140000) {
      return { maxDomain: 140000, ticks: [0, 35000, 70000, 105000, 140000] };
    }

    const power = Math.pow(10, Math.floor(Math.log10(max)));
    let maxDomain = Math.ceil(max / (power / 2)) * (power / 2);
    if (maxDomain < max) maxDomain = Math.ceil(max / power) * power;
    const step = maxDomain / 4;
    return {
      maxDomain,
      ticks: [0, step, step * 2, step * 3, maxDomain],
    };
  };

  const { maxDomain, ticks } = getNiceMaxAndTicks(maxVal);

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Usage By API Type</h3>
      <div className="w-full h-80 sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 10, right: 30, left: 10, bottom: 20 }}
            barCategoryGap={10}
          >
            <CartesianGrid horizontal={false} vertical={false} />
            <XAxis
              type="number"
              domain={[0, maxDomain]}
              ticks={ticks}
              tickLine={false}
              axisLine={{ stroke: "#E5E7EB" }}
              tick={{ fill: "#6B7280", fontSize: 12 }}
              tickFormatter={(val) => Number(val).toLocaleString()}
            />
            <YAxis
              type="category"
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#374151", fontSize: 12, fontWeight: 500 }}
              width={210}
            />
            <Tooltip
              formatter={(value) => [Number(value).toLocaleString(), "Calls"]}
              contentStyle={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                border: "1px solid #f3f4f6",
                padding: "8px 12px",
              }}
            />
            <Bar dataKey="value" barSize={22} radius={[0, 10, 10, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color || "#0E5E44"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default UsageByApiTypeChart;
